import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, Building2, CheckCircle2, DatabaseBackup,
  ExternalLink, Eye, Globe, HardDrive, Home, MousePointerClick, RefreshCw, Search, Settings,
  TrendingUp, UserPlus, Users, Wallet, type LucideIcon,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { formatUsd } from '../../lib/currency';
import { formatRelativeEs } from '../../lib/relative-time';
import {
  backupState, dashboardAlerts, diskUsage, formatBytes, pctChange, positionGain,
  type BackupJob, type BackupState, type DiskLevel, type SerpRow, type ServerStats, type SuperAdminDashboardData,
} from '../../lib/super-admin-dashboard';
import { CollapsibleSection } from './CollapsibleSection';

// Home for super admins. The agency dashboard is keyed off profile.agency_id,
// which is NULL for every super admin in production, so it rendered all zeros.
// This one reads platform numbers from get_super_admin_dashboard() instead.

const BIZHEALTH_URL = 'https://bizhealth.intercambioinmobiliario.com';

const num = (n: number | null | undefined) => (n ?? 0).toLocaleString('es-GT');
const day = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString('es-GT', { day: 'numeric', month: 'short' });
const monthYear = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString('es-GT', { month: 'short', year: 'numeric' });
const dateTime = (iso: string) => new Date(iso).toLocaleString('es-GT', {
  day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'America/Guatemala',
});

const BACKUP_PILL: Record<BackupState, { text: string; light: string; dark: string }> = {
  ok:      { text: 'Al día',       light: 'bg-emerald-50 text-emerald-700', dark: 'bg-emerald-500/10 text-emerald-300' },
  running: { text: 'En curso',     light: 'bg-blue-50 text-blue-700',       dark: 'bg-blue-500/10 text-blue-300' },
  stale:   { text: 'Atrasado',     light: 'bg-rose-50 text-rose-700',       dark: 'bg-rose-500/10 text-rose-300' },
  failed:  { text: 'Falló',        light: 'bg-rose-50 text-rose-700',       dark: 'bg-rose-500/10 text-rose-300' },
  never:   { text: 'Sin registro', light: 'bg-slate-100 text-slate-600',    dark: 'bg-white/5 text-slate-400' },
};

const Delta: React.FC<{ value: number | null; suffix?: string; title?: string }> = ({ value, suffix = '%', title }) => {
  if (value == null) return null;
  if (value === 0) return <span title={title} className="text-xs font-bold px-2 py-1 rounded-lg bg-slate-500/10 text-slate-400">Sin cambio</span>;
  const up = value > 0;
  return (
    <span title={title} className={clsx(
      'inline-flex items-center gap-0.5 text-xs font-bold px-2 py-1 rounded-lg whitespace-nowrap',
      up ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-400'
    )}>
      {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
      {Math.abs(value).toLocaleString('es-GT')}{suffix}
    </span>
  );
};

const Kpi: React.FC<{
  icon: LucideIcon; label: string; value: string; hint?: string; delta?: number | null; isLight: boolean;
}> = ({ icon: Icon, label, value, hint, delta, isLight }) => (
  <div className={clsx(
    'p-5 rounded-2xl border',
    isLight ? 'bg-white border-slate-100 shadow-sm' : 'glass-panel border-white/5'
  )}>
    <div className="flex justify-between items-start gap-2 mb-3">
      <span className={clsx('p-2.5 rounded-xl', isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/10 text-indigo-400')}>
        <Icon size={20} />
      </span>
      <Delta value={delta ?? null} title="Comparado con los 7 días anteriores" />
    </div>
    <p className="text-sm font-medium text-slate-400">{label}</p>
    <p className={clsx('text-3xl font-bold tracking-tight tabular-nums', isLight ? 'text-slate-900' : 'text-white')}>{value}</p>
    {hint && <p className={clsx('text-xs mt-2', isLight ? 'text-slate-500' : 'text-slate-400')}>{hint}</p>}
  </div>
);

const BackupCard: React.FC<{ backup: BackupJob; isLight: boolean }> = ({ backup, isLight }) => {
  const state = backupState(backup);
  const pill = BACKUP_PILL[state];
  const size = formatBytes(backup.size_bytes);
  return (
    <div className={clsx(
      'p-5 rounded-2xl border',
      isLight ? 'bg-white border-slate-100 shadow-sm' : 'glass-panel border-white/5',
      (state === 'stale' || state === 'failed') && (isLight ? 'border-rose-200' : 'border-rose-500/30')
    )}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className={clsx('font-bold', isLight ? 'text-slate-900' : 'text-white')}>{backup.label}</p>
        <span className={clsx('text-xs font-bold px-2 py-1 rounded-lg', isLight ? pill.light : pill.dark)}>{pill.text}</span>
      </div>
      {backup.last_success_at ? (
        <>
          <p className={clsx('text-2xl font-bold tracking-tight', isLight ? 'text-slate-900' : 'text-white')}>
            {formatRelativeEs(backup.last_success_at)}
          </p>
          <p className={clsx('text-xs mt-1', isLight ? 'text-slate-500' : 'text-slate-400')}>
            Último respaldo exitoso: {dateTime(backup.last_success_at)}{size && ` · ${size}`}
          </p>
        </>
      ) : (
        <p className={clsx('text-sm', isLight ? 'text-slate-500' : 'text-slate-400')}>Aún no hay un respaldo exitoso registrado.</p>
      )}
      {state === 'failed' && backup.last_error && (
        <p className="text-xs mt-2 text-rose-500">Último intento: {backup.last_error}</p>
      )}
      {backup.recent.length > 0 && (
        <div className="flex items-center gap-1.5 mt-4" aria-label="Últimas ejecuciones">
          {[...backup.recent].reverse().map(r => (
            <span
              key={r.at}
              title={`${dateTime(r.at)} — ${r.status === 'success' ? 'exitoso' : r.status === 'error' ? 'falló' : 'en curso'}`}
              className={clsx('w-2.5 h-2.5 rounded-full',
                r.status === 'success' ? 'bg-emerald-500' : r.status === 'error' ? 'bg-rose-500' : 'bg-slate-400')}
            />
          ))}
          <span className={clsx('text-[11px] ml-1', isLight ? 'text-slate-400' : 'text-slate-500')}>últimas ejecuciones</span>
        </div>
      )}
    </div>
  );
};

const DISK_PILL: Record<DiskLevel, { text: string; light: string; dark: string; bar: string }> = {
  ok:      { text: 'Suficiente',       light: 'bg-emerald-50 text-emerald-700', dark: 'bg-emerald-500/10 text-emerald-300', bar: 'bg-emerald-500' },
  warning: { text: 'Se está llenando', light: 'bg-amber-50 text-amber-700',     dark: 'bg-amber-500/10 text-amber-300',     bar: 'bg-amber-500' },
  danger:  { text: 'Casi lleno',       light: 'bg-rose-50 text-rose-700',       dark: 'bg-rose-500/10 text-rose-300',       bar: 'bg-rose-500' },
};

const StorageCard: React.FC<{ server: ServerStats | null | undefined; isLight: boolean }> = ({ server, isLight }) => {
  const disk = diskUsage(server);
  const pill = disk ? DISK_PILL[disk.level] : null;
  const sub = isLight ? 'text-slate-500' : 'text-slate-400';
  return (
    <div className={clsx(
      'p-5 rounded-2xl border',
      isLight ? 'bg-white border-slate-100 shadow-sm' : 'glass-panel border-white/5',
      disk?.level === 'danger' && (isLight ? 'border-rose-200' : 'border-rose-500/30')
    )}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className={clsx('font-bold flex items-center gap-2', isLight ? 'text-slate-900' : 'text-white')}>
          <HardDrive size={16} className="text-slate-400" /> Almacenamiento
        </p>
        <span className={clsx('text-xs font-bold px-2 py-1 rounded-lg',
          pill ? (isLight ? pill.light : pill.dark) : (isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/5 text-slate-400'))}>
          {pill ? pill.text : 'Sin datos'}
        </span>
      </div>
      {disk ? (
        <>
          <p className={clsx('text-2xl font-bold tracking-tight', isLight ? 'text-slate-900' : 'text-white')}>
            {formatBytes(disk.freeBytes)} libres
          </p>
          <div className={clsx('h-1.5 rounded-full overflow-hidden mt-3', isLight ? 'bg-slate-100' : 'bg-white/5')}
            role="progressbar" aria-valuenow={disk.usedPct} aria-valuemin={0} aria-valuemax={100} aria-label="Disco en uso">
            <div className={clsx('h-full rounded-full', pill!.bar)} style={{ width: `${disk.usedPct}%` }} />
          </div>
          <p className={clsx('text-xs mt-2', sub)}>
            {disk.usedPct}% en uso · {formatBytes(disk.totalBytes - disk.freeBytes)} de {formatBytes(disk.totalBytes)}
          </p>
        </>
      ) : (
        <p className={clsx('text-sm', sub)}>
          {server?.is_stale ? 'El servidor no ha reportado su disco en las últimas 3 horas.' : 'El servidor aún no ha reportado su disco.'}
        </p>
      )}
      {server && (
        <p className={clsx('text-xs mt-2', sub)}>
          Base de datos: {formatBytes(server.db_size_bytes)}
          {disk && server.reported_at && ` · medido ${formatRelativeEs(server.reported_at).toLowerCase()}`}
        </p>
      )}
    </div>
  );
};

const Position: React.FC<{ row: SerpRow; isLight: boolean }> = ({ row, isLight }) => (
  <span className="inline-flex items-center justify-end gap-2">
    <span className={clsx('font-bold tabular-nums', isLight ? 'text-slate-900' : 'text-white')}>
      {row.position != null ? row.position.toFixed(1) : '—'}
    </span>
    <Delta value={positionGain(row)} suffix="" title="Lugares ganados o perdidos frente a los 7 días anteriores" />
  </span>
);

export const SuperAdminDashboard: React.FC = () => {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const isLight = theme !== 'dark';

  const [data, setData] = useState<SuperAdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    const { data: res, error } = await supabase.rpc('get_super_admin_dashboard');
    if (res && !error) { setData(res as SuperAdminDashboardData); setFailed(false); }
    else setFailed(true);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const muted = isLight ? 'text-slate-500' : 'text-slate-400';
  const alerts = data ? dashboardAlerts(data) : [];
  const backupProblems = data
    ? data.backups.filter(b => ['stale', 'failed'].includes(backupState(b))).length
      + (diskUsage(data.server)?.level === 'danger' ? 1 : 0)
    : 0;
  const { signups, clients, leads, traffic, serp } = data ?? {} as Partial<SuperAdminDashboardData>;

  return (
    <div key="super-admin-dashboard" className="animate-page-entry">
      <div className="stagger-item flex flex-wrap justify-between items-end gap-3 mb-6" style={{ '--i': 0 } as React.CSSProperties}>
        <div>
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-black tracking-tight text-primary mb-1">
            Bienvenido, {profile?.full_name || 'Super Admin'}.
          </h3>
          <p className="text-sm lg:text-base text-secondary">
            Así está la plataforma hoy.
            {data && <span className={muted}> Actualizado: {dateTime(data.generated_at)}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            title="Actualizar"
            aria-label="Actualizar"
            className={clsx('p-3 rounded-xl border transition-colors disabled:opacity-50',
              isLight ? 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50' : 'border-white/10 text-slate-300 hover:bg-white/5')}
          >
            <RefreshCw size={18} className={clsx(loading && 'animate-spin')} />
          </button>
          <button
            onClick={() => navigate('/administrador')}
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-3 rounded-xl font-bold shadow-lg shadow-indigo-600/20 transition-all flex items-center gap-2"
          >
            <Settings size={18} /> Administración
          </button>
        </div>
      </div>

      {failed && (
        <div className={clsx('mb-4 flex flex-wrap items-center gap-3 px-5 py-4 rounded-2xl border',
          isLight ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-rose-500/10 border-rose-500/30 text-rose-200')}>
          <AlertTriangle size={18} className="shrink-0" />
          <span className="flex-1 text-sm font-medium">No se pudo cargar el tablero. Intenta de nuevo.</span>
          <button onClick={load} className="text-sm font-bold underline">Reintentar</button>
        </div>
      )}

      {!data && loading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className={clsx('h-36 rounded-2xl animate-pulse', isLight ? 'bg-slate-100' : 'bg-white/5')} />
          ))}
        </div>
      )}

      {data && (
        <>
          {/* What needs attention, or a single line saying nothing does */}
          {alerts.length > 0 ? (
            <div className="mb-4 space-y-2">
              {alerts.map(a => (
                <div key={a.text} className={clsx('flex items-start gap-3 px-5 py-3 rounded-2xl border-2 text-sm font-medium',
                  a.tone === 'danger'
                    ? (isLight ? 'bg-rose-50 border-rose-300 text-rose-900' : 'bg-rose-500/10 border-rose-500/40 text-rose-200')
                    : (isLight ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-amber-500/10 border-amber-500/40 text-amber-200'))}>
                  <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                  <span>{a.text}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className={clsx('mb-4 flex items-center gap-2 px-5 py-3 rounded-2xl border text-sm font-medium',
              isLight ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-emerald-500/10 border-emerald-500/25 text-emerald-200')}>
              <CheckCircle2 size={18} className="shrink-0" />
              Servidor, respaldos y tareas programadas al día.
            </div>
          )}

          <CollapsibleSection
            title="Servidor y respaldos" icon={DatabaseBackup} storageKey="sa_dash_respaldos" theme={theme} staggerIndex={1}
            badge={backupProblems > 0 ? { text: `${backupProblems} con problema`, tone: 'red' } : undefined}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {data.backups.map(b => <BackupCard key={b.job} backup={b} isLight={isLight} />)}
              <StorageCard server={data.server} isLight={isLight} />
            </div>
            <p className={clsx('text-xs mt-4', muted)}>
              Se espera un respaldo exitoso cada 24 horas. Si el servidor se cae o un respaldo falla, Healthchecks.io avisa por correo.
            </p>
          </CollapsibleSection>

          <CollapsibleSection title="Crecimiento" icon={TrendingUp} storageKey="sa_dash_crecimiento" theme={theme} staggerIndex={2}>
            <p className={clsx('text-xs mb-4', muted)}>Últimos 7 días, comparados con los 7 días anteriores.</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              <Kpi isLight={isLight} icon={UserPlus} label="Usuarios nuevos" value={num(signups?.users_7d)}
                delta={signups ? pctChange(signups.users_7d, signups.users_prev_7d) : null}
                hint={signups ? `${num(signups.users_prev_7d)} la semana anterior · ${num(signups.users_total)} en total` : undefined} />
              <Kpi isLight={isLight} icon={Building2} label="Agencias nuevas" value={num(signups?.agencies_7d)}
                delta={signups ? pctChange(signups.agencies_7d, signups.agencies_prev_7d) : null}
                hint={signups ? `${num(signups.agencies_prev_7d)} la semana anterior` : undefined} />
              <Kpi isLight={isLight} icon={Users} label="Clientes activos" value={num(clients?.active)}
                hint={clients ? `${num(clients.paying)} con plan de pago · ${num(clients.suspended)} suspendidos · ${num(clients.total)} en total` : undefined} />
              <Kpi isLight={isLight} icon={Wallet} label="Ingreso mensual recurrente"
                value={clients?.mrr_usd != null ? formatUsd(Math.round(clients.mrr_usd)) : '—'}
                delta={clients?.mrr_usd != null && clients.mrr_usd_prev != null ? pctChange(clients.mrr_usd, clients.mrr_usd_prev) : null}
                hint={clients?.snapshot_date ? `Corte del ${day(clients.snapshot_date)}` : 'Sin datos todavía'} />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Portales" icon={Globe} storageKey="sa_dash_portales" theme={theme} staggerIndex={3}>
            <p className={clsx('text-xs mb-4', muted)}>
              Semana contra semana anterior. Analytics y Google publican con 2 a 3 días de atraso, por eso cada tarjeta indica su semana.
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              <Kpi isLight={isLight} icon={Users} label="Prospectos generados" value={num(leads?.portal_7d)}
                delta={leads ? pctChange(leads.portal_7d, leads.portal_prev_7d) : null}
                hint={leads ? `Últimos 7 días · ${num(leads.portal_total)} desde ${leads.since ? monthYear(leads.since) : 'el inicio'}` : undefined} />
              <Kpi isLight={isLight} icon={Eye} label="Páginas vistas"
                value={traffic?.has_data ? num(traffic.page_views_7d) : '—'}
                delta={traffic?.has_data ? pctChange(traffic.page_views_7d, traffic.page_views_prev_7d) : null}
                hint={traffic?.has_data ? `${day(traffic.from)} – ${day(traffic.through)} · ${num(traffic.sessions_7d)} visitas` : 'Sin datos de Analytics'} />
              <Kpi isLight={isLight} icon={Home} label="Vistas de propiedades"
                value={traffic?.has_data ? num(traffic.property_views_7d) : '—'}
                delta={traffic?.has_data ? pctChange(traffic.property_views_7d, traffic.property_views_prev_7d) : null}
                hint={traffic?.has_data ? `${day(traffic.from)} – ${day(traffic.through)}` : 'Sin datos de Analytics'} />
              <Kpi isLight={isLight} icon={MousePointerClick} label="Clics desde Google"
                value={serp?.has_data ? num(serp.clicks_7d) : '—'}
                delta={serp?.has_data ? pctChange(serp.clicks_7d, serp.clicks_prev_7d) : null}
                hint={serp?.has_data ? `${day(serp.from)} – ${day(serp.through)} · ${num(serp.impressions_7d)} impresiones` : 'Sin datos de Search Console'} />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Posiciones en Google" icon={Search} storageKey="sa_dash_serp" theme={theme} staggerIndex={4}>
            {serp?.has_data ? (
              <>
                <p className={clsx('text-xs mb-4', muted)}>
                  Posición promedio del {day(serp.from)} al {day(serp.through)}. Un número menor es mejor; la flecha indica los lugares ganados o perdidos frente a la semana anterior.
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6">
                  <div className={clsx('p-5 rounded-2xl border', isLight ? 'bg-white border-slate-100 shadow-sm' : 'glass-panel border-white/5')}>
                    <p className="text-sm font-medium text-slate-400 mb-3">Por portal</p>
                    <ul className="space-y-3">
                      {serp.portals.map(p => (
                        <li key={p.site} className="flex items-center justify-between gap-3 text-sm">
                          <span className="min-w-0">
                            <span className={clsx('block font-medium truncate', isLight ? 'text-slate-700' : 'text-slate-200')}>{p.site}</span>
                            <span className={clsx('text-xs', muted)}>{num(p.clicks_7d)} clics</span>
                          </span>
                          <Position row={p} isLight={isLight} />
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={clsx('lg:col-span-2 p-5 rounded-2xl border', isLight ? 'bg-white border-slate-100 shadow-sm' : 'glass-panel border-white/5')}>
                    <p className="text-sm font-medium text-slate-400 mb-3">Búsquedas principales en mapainmueble.com</p>
                    <ul className={clsx('divide-y', isLight ? 'divide-slate-100' : 'divide-white/5')}>
                      {serp.keywords.map(k => (
                        <li key={k.query} className="flex items-center justify-between gap-3 py-2 text-sm">
                          <span className={clsx('min-w-0 truncate', isLight ? 'text-slate-700' : 'text-slate-200')}>{k.query}</span>
                          <Position row={k} isLight={isLight} />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </>
            ) : (
              <p className={clsx('text-sm', muted)}>Sin datos de Search Console.</p>
            )}
          </CollapsibleSection>

          <a
            href={BIZHEALTH_URL} target="_blank" rel="noopener noreferrer"
            className={clsx('inline-flex items-center gap-1.5 text-sm font-bold mt-2 mb-8', isLight ? 'text-indigo-600 hover:text-indigo-500' : 'text-indigo-400 hover:text-indigo-300')}
          >
            Ver el detalle en BizHealth <ExternalLink size={14} />
          </a>
        </>
      )}
    </div>
  );
};
